import emailjs from '@emailjs/browser';

export interface OrderNotification {
  customerName: string;
  customerEmail: string;
  orderTotal: number;
  orderId: string;
  modules: string[];
  discountApplied: boolean;
  discountPercent?: number;
  totalHours: number;
}

export interface QuestionnaireAssignmentNotification {
  recipientEmail: string;
  recipientName?: string;
  questionnaireTitle: string;
  templateName: string;
  deadline: Date;
  assignedByName: string;
  assignmentId: string;
}

export const adminNotificationService = {
  /**
   * Send email notification to admins when a new order is submitted
   * @param notification Order notification data
   * @returns Promise<boolean> Success status
   */
  async sendNewOrderNotification(notification: OrderNotification): Promise<boolean> {
    try {
      const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
      const templateId = import.meta.env.VITE_EMAILJS_ADMIN_TEMPLATE_ID;
      const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey) {
        console.warn('⚠️ EmailJS not configured. Skipping admin notification.');
        console.warn('Missing env vars:', {
          serviceId: !serviceId,
          templateId: !templateId,
          publicKey: !publicKey
        });
        return false;
      }

      const templateParams = {
        customer_name: notification.customerName,
        customer_email: notification.customerEmail,
        order_total: notification.orderTotal.toFixed(2),
        order_id: notification.orderId,
        modules: notification.modules.join(', '),
        discount_applied: notification.discountApplied ? 'Ja' : 'Nein',
        discount_percent: notification.discountPercent || 0,
        total_hours: notification.totalHours,
        dashboard_link: `${window.location.origin}/quotes/${notification.orderId}`,
        to_email: import.meta.env.VITE_ADMIN_EMAIL || 'emanuel@chimperator-live.de'
      };

      console.log('📧 Sending admin notification email...', templateParams);

      const response = await emailjs.send(
        serviceId,
        templateId,
        templateParams,
        publicKey
      );

      if (response.status === 200) {
        console.log('✅ Admin notification email sent successfully');
        return true;
      } else {
        console.error('❌ Failed to send admin notification:', response);
        return false;
      }
    } catch (error) {
      console.error('❌ Error sending admin notification:', error);
      // Don't throw - we don't want to block the user flow if email fails
      return false;
    }
  },

  /**
   * Send questionnaire assignment invitation to team member
   * @param notification Questionnaire assignment data
   * @returns Promise<boolean> Success status
   */
  async sendQuestionnaireInvitation(notification: QuestionnaireAssignmentNotification): Promise<boolean> {
    try {
      const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
      const templateId = import.meta.env.VITE_EMAILJS_QUESTIONNAIRE_TEMPLATE_ID;
      const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey) {
        console.warn('⚠️ EmailJS questionnaire template not configured. Skipping invitation email.');
        console.warn('Missing env vars:', {
          serviceId: !serviceId,
          templateId: !templateId,
          publicKey: !publicKey
        });
        return false;
      }

      const templateParams = {
        to_email: notification.recipientEmail,
        recipient_name: notification.recipientName || notification.recipientEmail.split('@')[0],
        questionnaire_title: notification.questionnaireTitle,
        template_name: notification.templateName,
        deadline_formatted: notification.deadline.toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),
        assigned_by_name: notification.assignedByName,
        questionnaire_link: `${window.location.origin}/questionnaire/${notification.assignmentId}`,
        app_url: window.location.origin
      };

      console.log('📧 Sending questionnaire invitation to:', notification.recipientEmail);

      const response = await emailjs.send(serviceId, templateId, templateParams, publicKey);

      if (response.status === 200) {
        console.log('✅ Questionnaire invitation sent to:', notification.recipientEmail);
        return true;
      } else {
        console.error('❌ Failed to send questionnaire invitation:', response);
        return false;
      }
    } catch (error) {
      console.error('❌ Error sending questionnaire invitation:', error);
      // Don't throw - we don't want to block the user flow if email fails
      return false;
    }
  }
};

